import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import { CommissionOption, RuleCondition } from '~/consts/agentHub';

import { useListUsers } from '~/hooks/apis/agent/useListUsers';
import useCurrentBrand from '~/hooks/useCurrentBrand';

interface Props {
  setCreatedConditions: (condition: RuleCondition, replace?: boolean) => void;
  existingConditions: RuleCondition['value'];
}

function AgentHubCommissionsAgentInput({ setCreatedConditions, existingConditions }: Props) {
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedAgents, setSelectedAgents] = useState<CommissionOption>(existingConditions || []);
  const brand = useCurrentBrand();

  const { data: usersData, isFetching } = useListUsers(debouncedSearch, brand);
  const result = useMemo(() => {
    if (!usersData) {
      return [];
    }
    return usersData.map((agent) => ({
      value: agent.id_member,
      label: `${agent.full_name || ' '} [${agent.email}]`,
    }));
  }, [usersData]);

  const noOptionsText = useMemo(() => {
    if (!debouncedSearch) {
      return 'Start typing to search for agents';
    }
    if (usersData?.length === 0) {
      return 'No agents found';
    }
    return 'Start typing to search for agents';
  }, [debouncedSearch, usersData?.length]);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, 500),
    [],
  );

  const handleSaveRule = () => {
    setCreatedConditions(
      {
        type: 'agent',
        value: selectedAgents,
      },
      true,
    );
  };

  useEffect(() => {
    handleSaveRule();
  }, [selectedAgents]);

  const handleInputChange = (_: ChangeEvent, newInputValue: string) => {
    if (newInputValue) {
      debouncedSetSearch(newInputValue);
    }
  };

  const handleChange = (_: ChangeEvent, value: CommissionOption) => {
    setSelectedAgents(value);
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Autocomplete
          multiple
          options={result}
          loading={isFetching}
          getOptionLabel={(option) => option.label}
          defaultValue={[]}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField {...params} label="Agents" placeholder={selectedAgents.length > 0 ? '' : 'Agents'} />
          )}
          onInputChange={handleInputChange}
          value={selectedAgents}
          onChange={handleChange}
          noOptionsText={noOptionsText}
          disablePortal
        />
        <Typography variant="caption" color="textSecondary">
          Start typing to search for agents
        </Typography>
      </Box>
    </Stack>
  );
}

export default AgentHubCommissionsAgentInput;
